import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useToast } from '@/hooks/use-toast';
import { Crown, FileText, Check, CreditCard, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { trackCheckoutStarted } from '@/utils/amplitudeEvents';
import { useCompany } from '@/contexts/CompanyContext';
import { useAuth } from '@/contexts/AuthContext';
const BillingSettings = () => {
  const [showFeatures, setShowFeatures] = useState(false);
  const { 
    subscriptionTier, 
    trialEndsAt, 
    createCheckoutSession,
    openCustomerPortal,
    loading 
  } = useSubscription();
  const { toast } = useToast();
  const { currentCompany } = useCompany();
  const { user } = useAuth();

  const getTrialDaysRemaining = () => {
    if (!trialEndsAt) return 0;
    const diff = new Date(trialEndsAt).getTime() - new Date().getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const daysRemaining = getTrialDaysRemaining();
  const isEffectivelyExpired = subscriptionTier === 'expired' || (subscriptionTier === 'trial' && daysRemaining <= 0);
  const isActiveTrial = subscriptionTier === 'trial' && daysRemaining > 0;
  const isPaidPlan = subscriptionTier === 'pro' || subscriptionTier === 'enterprise';

  const handleUpgrade = async () => {
    try {
      trackCheckoutStarted({
        company_id: currentCompany?.company_id || null,
        company_name: currentCompany?.company_name || null,
        user_id: user?.id || null,
        user_email: user?.email || null,
        user_name: user?.user_metadata?.full_name || null,
        current_plan: subscriptionTier,
        target_plan: 'Pro',
        trial_days_remaining: daysRemaining > 0 ? daysRemaining : null,
        source: 'billing_settings',
      });
      await createCheckoutSession('auto', 'Pro');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to start checkout. Please try again.",
        variant: "destructive"
      });
    }
  };

  const proFeatures = [
    'Unlimited behavioral assessments',
    'Unlimited cognitive assessments',
    'AI-powered personality insights',
    'Talent optimization tools',
    'Role matching & ideal profiles',
    'Org chart builder',
    'Work behavior analysis',
    'Training resources',
    'PDF exports'
  ];

  const getPlanLabel = () => {
    if (isPaidPlan) return 'Pro';
    if (isActiveTrial) return 'Trial';
    if (subscriptionTier === 'free') return 'Free';
    return 'Expired';
  };

  const getStatusBadge = () => {
    if (isPaidPlan) return <Badge className="bg-primary/10 text-primary border-0">Active</Badge>;
    if (isActiveTrial) return <Badge variant="secondary">{daysRemaining} days left</Badge>;
    if (subscriptionTier === 'free') return <Badge variant="secondary">Free</Badge>;
    return <Badge variant="destructive">Expired</Badge>;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Billing</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your subscription and billing information
        </p>
      </div>

      {/* Current Plan */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Current Plan
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xl font-semibold">{getPlanLabel()}</span>
              {getStatusBadge()}
            </div>
            {isPaidPlan && (
              <Button variant="outline" size="sm" onClick={() => openCustomerPortal()}>
                Manage Subscription
              </Button>
            )}
          </div>
          {isActiveTrial && (
            <p className="text-sm text-muted-foreground mt-2">
              Your trial ends on {new Date(trialEndsAt!).toLocaleDateString()}
            </p>
          )}
          {isEffectivelyExpired && (
            <p className="text-sm text-muted-foreground mt-2">
              Subscribe to a plan below to regain full access to the platform.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Upgrade CTA for trial/expired users */}
      {(!isPaidPlan && (isActiveTrial || isEffectivelyExpired)) && (
        <Card className={isEffectivelyExpired ? 'border-primary' : ''}>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-muted-foreground" />
                <span className="text-lg font-semibold">Upgrade to Pro</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-xl font-bold">$199</span>
                <span className="text-sm text-muted-foreground">/month</span>
              </div>
            </div>
            
            <Button 
              onClick={handleUpgrade} 
              className="w-full" 
              size="lg"
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Start Pro Plan'}
            </Button>

            <button
              type="button"
              onClick={() => setShowFeatures(!showFeatures)}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mx-auto"
            >
              What's included
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${showFeatures ? 'rotate-180' : ''}`} />
            </button>

            {showFeatures && (
              <div className="grid gap-2 pt-2 border-t">
                {proFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-primary" />
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Usage Overview - hidden for now */}

      {/* Billing History - hidden for now */}
    </div>
  );
};

export default BillingSettings;
